import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
    { label: "Home", target: "hero" },
    { label: "Highlights", target: "highlights" },
    { label: "Profile", target: "profile" },
    { label: "Academics", target: "academics" },
    { label: "Results", target: "results" },
    { label: "About", target: "about" },
    { label: "Schedule", target: "schedule" },
    { label: "Contact", target: "contact" },
];

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const jump = (id) => {
        setOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <>
            <motion.header
                className={`fixed top-0 inset-x-0 z-50 transition-colors ${
                    scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
                }`}
                initial={{ y: -40, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
            >
                <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-14 flex items-center justify-between">
                    <button
                        onClick={() => jump("hero")}
                        className="font-mono text-xs tracking-[0.25em] uppercase flex items-center gap-2"
                        data-testid="nav-logo"
                    >
                        <span className="inline-block w-2 h-2 bg-brand-gold" />
                        JAI&apos;REL YOUNG
                        <span className="text-white/40 hidden md:inline">/ WRESTLING RECRUIT</span>
                    </button>

                    <nav className="hidden lg:flex items-center gap-7">
                        {NAV_ITEMS.map((n) => (
                            <button
                                key={n.target}
                                onClick={() => jump(n.target)}
                                className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/70 hover:text-brand-gold transition-colors"
                                data-testid={`nav-${n.target}`}
                            >
                                {n.label}
                            </button>
                        ))}
                    </nav>

                    <button
                        className="lg:hidden text-white p-2"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="nav-toggle"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl lg:hidden pt-20 px-6"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <nav className="flex flex-col gap-6" data-testid="mobile-nav">
                            {NAV_ITEMS.map((n, i) => (
                                <motion.button
                                    key={n.target}
                                    onClick={() => jump(n.target)}
                                    className="text-left font-mono uppercase tracking-[0.15em] text-2xl border-b border-white/10 pb-4"
                                    initial={{ x: -20, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    transition={{ delay: 0.05 * i }}
                                >
                                    <span className="text-brand-gold text-xs mr-3">0{i + 1}</span>
                                    {n.label}
                                </motion.button>
                            ))}
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
